/* eslint-disable */
import dayjs from "dayjs";
import {
  ChangeRequest,
  ChangeRequestComputedStatusEnum,
  ChangeRequestTableRow,
  ChangeRequestTableRows,
  RemoveSupplyComputedStatusEnum,
  RequestTypeEnum,
  SortByEnum,
} from "./types";
import { apiClient } from "src/api/apiService";
import { getFormattedAddress } from "../Premises/helpers";
import { OperationalTeamEnum } from "../Premises/types";

export const formatDate = (dateString: string): string => {
  const date = dayjs(dateString);
  return date.format("DD.MM.YYYY");
};

export const getStatusIconString = (
  requestStatus:
    | ChangeRequestComputedStatusEnum
    | RemoveSupplyComputedStatusEnum,
  requestType?: RequestTypeEnum | RequestTypeEnum.CHANGE_SUPPLY
): string => {
  return requestType === RequestTypeEnum.REMOVE_SUPPLY
    ? getRemoveSupplyStatusIconString(
        requestStatus as RemoveSupplyComputedStatusEnum
      )
    : getChangeSupplyStatusIconString(
        requestStatus as ChangeRequestComputedStatusEnum
      );
};

export const getChangeSupplyStatusIconString = (
  requestStatus: ChangeRequestComputedStatusEnum
): string => {
  const statusMessages = {
    [ChangeRequestComputedStatusEnum.COMPLETED]: "tick",
    [ChangeRequestComputedStatusEnum.DRAFT]: "pencil",
    [ChangeRequestComputedStatusEnum.CANCELLED]: "cancel",
    [ChangeRequestComputedStatusEnum.IN_PROGRESS]: "clock",
    [ChangeRequestComputedStatusEnum.RECEIVED]: "clock",
    [ChangeRequestComputedStatusEnum.SCHEDULED]: "clock",
  };

  return statusMessages[requestStatus as keyof typeof statusMessages] || "none";
};

export const getRemoveSupplyStatusIconString = (
  requestStatus: RemoveSupplyComputedStatusEnum
): string => {
  const statusMessages = {
    [RemoveSupplyComputedStatusEnum.COMPLETED]: "tick",
    [RemoveSupplyComputedStatusEnum.DRAFT]: "pencil",
    [RemoveSupplyComputedStatusEnum.CANCELLED]: "cancel",
    [RemoveSupplyComputedStatusEnum.IN_PROGRESS]: "clock",
    [RemoveSupplyComputedStatusEnum.RECEIVED]: "clock",
    [RemoveSupplyComputedStatusEnum.VERIFICATION_SENT]: "clock",
    [RemoveSupplyComputedStatusEnum.VERIFICATION_COMPLETED]: "clock",
  };

  return statusMessages[requestStatus as keyof typeof statusMessages] || "none";
};

export const mapRequestsToRows = (
  requests: ChangeRequest[]
): ChangeRequestTableRows => {
  return requests.map((request) => {
    const addressObj = request.premise;
    const premiseRequestSummary = request.premise_request_summary;
    const address = getFormattedAddress(addressObj);
    const isDraft =
      premiseRequestSummary.request_status ===
        ChangeRequestComputedStatusEnum.DRAFT ||
      premiseRequestSummary.request_status ===
        RemoveSupplyComputedStatusEnum.DRAFT;
    return {
      serviceType: premiseRequestSummary.service_type,
      connectionType: premiseRequestSummary.connection_type,
      address,
      id: premiseRequestSummary.request_id,
      journeyId: premiseRequestSummary.journey_id,
      distributorApplicationId:
        premiseRequestSummary.distributor_application_id,
      nmi: premiseRequestSummary.service_identifier,
      lodgedDate: isDraft ? null : formatDate(premiseRequestSummary.submitted),
      etaDate: formatDate(premiseRequestSummary.expected),
      updatedDate: isDraft ? null : formatDate(premiseRequestSummary.updated),
      status: getStatusIconString(
        premiseRequestSummary.request_status,
        premiseRequestSummary.request_type
      ),
      statusText: getStatusText(
        premiseRequestSummary.request_status,
        premiseRequestSummary.scheduled_start_date,
        premiseRequestSummary.request_type
      ),
      requestStatus: premiseRequestSummary.request_status,
      state: addressObj.site_details.state,
      requestChannel: premiseRequestSummary.request_channel,
      requestType: premiseRequestSummary.request_type,
      operationalTeam: getOperationalTeamText(
        premiseRequestSummary.operational_team
      ),
    };
  });
};

export const normaliseIdColumnValue = (
  requestId: string,
  journeyId?: string | null,
  distributorApplicationId?: string | null
): string => {
  if (journeyId) {
    return `J ${journeyId}`;
  }
  if (distributorApplicationId) {
    return `DB ${distributorApplicationId}`;
  }
  return requestId;
};

export const mapPremisesTableRowKeyToSortByEnum = (
  key: keyof ChangeRequestTableRow
): SortByEnum | null => {
  const keyToSortByEnumMap: Record<
    keyof ChangeRequestTableRow,
    SortByEnum | null
  > = {
    serviceType: SortByEnum.SERVICE_TYPE,
    connectionType: SortByEnum.CONNECTION_TYPE,
    address: SortByEnum.ADDRESS,
    id: SortByEnum.REQUEST_ID,
    nmi: null,
    lodgedDate: SortByEnum.SUBMITTED,
    updatedDate: SortByEnum.UPDATED,
    status: SortByEnum.REQUEST_STATUS,
    statusText: SortByEnum.REQUEST_STATUS,
    journeyId: null,
    distributorApplicationId: null,
    requestStatus: null,
    state: null,
    requestChannel: null,
    requestType: null,
    operationalTeam: null,
  };
  return keyToSortByEnumMap[key] || null;
};

export const getOperationalTeamText = (
  operationalTeamEnum: OperationalTeamEnum | null
): string => {
  switch (operationalTeamEnum) {
    case OperationalTeamEnum.TIER_1:
      return "Tier 1";
    case OperationalTeamEnum.TIER_2:
      return "Tier 2";
    case OperationalTeamEnum.TIER_3:
      return "Tier 3";
    case OperationalTeamEnum.CES:
      return "CES";
    case OperationalTeamEnum.MSBG:
      return "MSBG";
    case OperationalTeamEnum.MSBG_COUNCIL:
      return "MSBG Council";
    case OperationalTeamEnum.MSBG_TELCO:
      return "MSBG Telco";
    default:
      return "-";
  }
};

export const getStatusText = (
  requestStatus:
    | ChangeRequestComputedStatusEnum
    | RemoveSupplyComputedStatusEnum,
  scheduledStartDate?: string,
  requestType?: RequestTypeEnum | RequestTypeEnum.CHANGE_SUPPLY
): string => {
  return requestType === RequestTypeEnum.REMOVE_SUPPLY
    ? getRemoveSupplyStatusText(requestStatus as RemoveSupplyComputedStatusEnum)
    : getChangeSupplyStatusText(
        requestStatus as ChangeRequestComputedStatusEnum,
        scheduledStartDate
      );
};

export const getChangeSupplyStatusText = (
  requestStatus: ChangeRequestComputedStatusEnum,
  scheduledStartDate?: string
): string => {
  const changeSupplyStatusMessages = {
    [ChangeRequestComputedStatusEnum.DRAFT]: "Draft",
    [ChangeRequestComputedStatusEnum.RECEIVED]: "We've received your request",
    [ChangeRequestComputedStatusEnum.IN_PROGRESS]: "Change work requested",
    [ChangeRequestComputedStatusEnum.COMPLETED]: "All done",
    [ChangeRequestComputedStatusEnum.CANCELLED]: "Cancelled",
    [ChangeRequestComputedStatusEnum.SCHEDULED]: `Change work scheduled for ${
      scheduledStartDate && formatDate(scheduledStartDate)
    }`,
  };
  return changeSupplyStatusMessages[requestStatus] || "";
};

export const getRemoveSupplyStatusText = (
  requestStatus: RemoveSupplyComputedStatusEnum
): string => {
  const removeSupplyStatusMessages = {
    [RemoveSupplyComputedStatusEnum.DRAFT]: "Draft",
    [RemoveSupplyComputedStatusEnum.RECEIVED]: "Remove supply received",
    [RemoveSupplyComputedStatusEnum.VERIFICATION_SENT]:
      "Pending account holder request",
    [RemoveSupplyComputedStatusEnum.VERIFICATION_COMPLETED]:
      "Account holder request received",
    [RemoveSupplyComputedStatusEnum.IN_PROGRESS]: "Remove supply requested",
    [RemoveSupplyComputedStatusEnum.COMPLETED]: "All done",
    [RemoveSupplyComputedStatusEnum.CANCELLED]: "Cancelled",
  };
  return removeSupplyStatusMessages[requestStatus] || "";
};

/* eslint-enable */

export const isAllowedStateForElec = (state: string) => {
  const allowedStates = ["ACT", "NSW", "SA", "VIC"];
  return allowedStates.includes(state);
};

export const isAllowedForQldElec = (postcode: string) => {
  const allowedPostcodes = [
    "4390",
    "4385",
    "4384",
    "4387",
    "4383",
    "4380",
    "4496",
    "4494",
  ];

  return allowedPostcodes.includes(postcode);
};

interface IAddressObject {
  lot_number: string;
  street_number: string;
  street_name: string;
  street_suffix: string;
  street_type: string;
  postcode: string;
  dp_number: string;
  suburb: string;
  state: string;
  building_name: string;
}

export const fromAddressObjectToString = (address: IAddressObject) => {
  return `${address.street_number} ${address.street_name} ${address.street_suffix} ${address.street_type}, ${address.suburb}, ${address.state} ${address.postcode} `;
};

export const updateToNotRequired = async (
  journeyId: string | null,
  reFetchPremises: () => void
) => {
  if (!journeyId) {
    return;
  }
  try {
    await apiClient.put(`/close-transfer/${journeyId}`);
    reFetchPremises();
  } catch (error) {
    console.log(error);
  }
};
